/**
 * Science site → Flow: the routes readers take — the page they landed on, then
 * the next, then the next — as a Sankey. A band is as wide as the sessions that
 * took that step; "(left)" is where they stopped.
 *
 * Built from the raw events, the only record of the ORDER of pages, which are
 * kept 14 days — so the chart looks back no further, whatever the window. The
 * science site's filter bar narrows it like every other tab.
 * See backend `admin/analytics/siteFlow.ts`.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, Sankey, Tooltip } from 'recharts';
import { InfoTip } from '@shared-ui/components';
import { siteFlowApi, type SiteScope } from '../../../api/siteAnalyticsApi';

const COLOURS = ['#6366f1', '#8b5cf6', '#a855f7', '#c026d3', '#db2777', '#e11d48', '#ea580c', '#ca8a04'];
const LEFT = '(left)';

interface FlowNodeDatum { name: string; step: number; sessions: number; colour: string }

/** A node: its bar, and its page and count beside it — to the right, except in
 *  the last column, where the label sits to the left so it stays on the chart. */
function FlowNode(props: { x: number; y: number; width: number; height: number; payload: FlowNodeDatum; containerWidth?: number }) {
  const { x, y, width, height, payload } = props;
  const lastColumn = x > (props.containerWidth ?? 900) * 0.7;
  const label = `${payload.name} · ${payload.sessions.toLocaleString()}`;
  return (
    <g>
      <rect x={x} y={y} width={width} height={Math.max(1, height)} fill={payload.colour} rx={2} />
      {height >= 10 && (
        <text x={lastColumn ? x - 6 : x + width + 6} y={y + height / 2} dominantBaseline="central"
          textAnchor={lastColumn ? 'end' : 'start'} className="sf-label">
          {label.length > 44 ? `${label.slice(0, 43)}…` : label}
        </text>
      )}
    </g>
  );
}

export default function SiteFlowChart({ from, to, scope, site = 'science' }: {
  from: Date; to: Date; scope: SiteScope | Record<string, string>;
  /** Which property's routes: the science site's pages, or the app's screens. */
  site?: 'science' | 'app';
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['site-flow', site, from.toISOString(), to.toISOString(), scope],
    queryFn: () => siteFlowApi.get(from, to, scope as Record<string, string>, site),
  });
  const noun = site === 'app' ? 'screen' : 'page';
  const who = site === 'app' ? 'students' : 'readers';

  const chart = useMemo(() => {
    if (!data) return null;
    // A landing page keeps its colour down the columns, so a route can be followed.
    const colourOf = new Map<string, string>();
    data.nodes.filter(n => n.step === 0).forEach((n, i) => colourOf.set(n.page, COLOURS[i % COLOURS.length]));
    const index = new Map(data.nodes.map((n, i) => [n.id, i]));
    return {
      nodes: data.nodes.map((n): FlowNodeDatum => ({
        name: n.page, step: n.step, sessions: n.sessions,
        colour: n.page === LEFT ? 'var(--text-tertiary)' : colourOf.get(n.page) ?? COLOURS[(n.step + 3) % COLOURS.length],
      })),
      links: data.links
        .filter(l => index.has(l.source) && index.has(l.target))
        .map(l => ({ source: index.get(l.source)!, target: index.get(l.target)!, value: l.sessions })),
    };
  }, [data]);

  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  return (
    <section className="sa-panel">
      <h2>
        Where {who} went next
        <InfoTip>
          <span className="sa-tip-p">
            Each session’s {noun}s in order: the {noun} it landed on, then the next, up to four. A band is as wide
            as the sessions that took that step; “(left)” is where they stopped. A reload is not a step. Past
            the busiest pages at each step, the rest share “Other pages”.{site === 'app' ? ' A screen is its route — every question is /question/:id — and only sessions recorded while session tracing was on are here.' : ''}
          </span>
          <span className="sa-tip-p">
            Only the last 14 days: the order of pages is kept that long. Narrowed by the filters above, like
            the other tabs.
          </span>
        </InfoTip>
      </h2>
      {isLoading && <p className="sa-empty">Loading…</p>}
      {error && <p className="sa-error">{(error as Error).message}</p>}
      {data && site === 'app' && data.site !== 'app' && (
        <p className="sa-empty">This backend does not draw the app’s flow yet — it needs the latest deploy.</p>
      )}
      {data && (site !== 'app' || data.site === 'app') && (
        data.sessions === 0 || !chart || chart.links.length === 0
          ? <p className="sa-empty">No sessions with page views in {fmtDay(data.from)} – {fmtDay(data.to)}.</p>
          : (
            <>
              <p className="sa-note">
                {data.sessions.toLocaleString()} sessions, {fmtDay(data.from)} – {fmtDay(data.to)}
                {data.clamped && ' — the window is longer, but the order of pages is kept only 14 days'}.
              </p>
              <div className="sf-steps">
                {['Landed on', `2nd ${noun}`, `3rd ${noun}`, `4th ${noun}`].map(s => <span key={s}>{s}</span>)}
              </div>
              <div className="sf-chart">
                <ResponsiveContainer width="100%" height={560}>
                  <Sankey
                    data={chart}
                    nodeWidth={10}
                    nodePadding={18}
                    linkCurvature={0.5}
                    iterations={32}
                    margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    node={p => <FlowNode {...(p as unknown as Parameters<typeof FlowNode>[0])} />}
                    link={{ stroke: 'var(--text-tertiary)', strokeOpacity: 0.25 }}
                  >
                    <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} />
                  </Sankey>
                </ResponsiveContainer>
              </div>
            </>
          )
      )}
    </section>
  );
}
